/**
 * AnimatedSpinner — branded rotating gradient ring loader.
 *
 * Replaces RN's stock <ActivityIndicator/> with a Tago-branded loader that
 * matches the marketing-site aesthetic: purple→coral→amber gradient sweep
 * around an arc, rotating at 1.2s/turn. Same Reanimated + SVG dependency
 * footprint as AnimatedCheckmark / AnimatedX.
 *
 * Reduced-motion path: static gradient ring (no rotation), no animation
 * footprint, identical visual.
 *
 * @example
 *   <AnimatedSpinner size="md" />
 *   <AnimatedSpinner size={64} />            // explicit size in px
 *   <AnimatedSpinner size="lg" message={t('common.loading')} />
 */

import { useEffect, useId } from 'react'
import { View, Text, type ViewStyle } from 'react-native'
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withRepeat,
  withTiming,
  cancelAnimation,
  useReducedMotion,
  Easing,
} from 'react-native-reanimated'
import Svg, { Circle, Defs, LinearGradient, Stop } from 'react-native-svg'
import { COLORS, FONTS } from '../../lib/brand'
import { useSemanticColors, SPACING } from '../../lib/theme/tokens'
import { useTheme } from '../../lib/hooks/use-theme'

type SpinnerSize = 'sm' | 'md' | 'lg' | number

const SIZE_MAP: Record<Exclude<SpinnerSize, number>, number> = {
  sm: 32,
  md: 56,
  lg: 96,
}

const ROTATION_DURATION_MS = 1200

interface AnimatedSpinnerProps {
  size?: SpinnerSize
  /** Optional caption underneath. Spaced 12px below the ring. */
  message?: string
  /** Wrap with vertical centering. Default true. */
  centered?: boolean
  style?: ViewStyle
  /** Override stroke. Defaults to a coral→purple gradient. */
  color?: string
  testID?: string
}

export function AnimatedSpinner({
  size = 'md',
  message,
  centered = true,
  style,
  color,
  testID,
}: AnimatedSpinnerProps) {
  const { isDark } = useTheme()
  const colors = useSemanticColors(isDark)
  const reducedMotion = useReducedMotion()
  const gradientId = useId().replace(/:/g, '')

  const dimension = typeof size === 'number' ? size : SIZE_MAP[size]
  const rotation = useSharedValue(0)

  useEffect(() => {
    if (reducedMotion) {
      rotation.value = 0
      return
    }
    rotation.value = withRepeat(
      withTiming(360, {
        duration: ROTATION_DURATION_MS,
        easing: Easing.linear,
      }),
      -1,
      false,
    )
    return () => {
      cancelAnimation(rotation)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reducedMotion])

  const ringStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }))

  const strokeWidth = Math.max(3, dimension / 12)
  const radius = (dimension - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  // Show ~70% of the ring (gap reveals the rotation visually).
  const dashLength = circumference * 0.7
  const gapLength = circumference - dashLength

  const wrapperBase: ViewStyle = centered ? { alignItems: 'center', justifyContent: 'center' } : {}

  return (
    <View style={[wrapperBase, style]} testID={testID}>
      <Animated.View style={[{ width: dimension, height: dimension }, ringStyle]}>
        <Svg width={dimension} height={dimension}>
          <Defs>
            <LinearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
              <Stop offset="0%" stopColor={color ?? COLORS.coral.DEFAULT} stopOpacity="1" />
              <Stop offset="60%" stopColor={color ?? COLORS.amber.DEFAULT} stopOpacity="0.85" />
              <Stop offset="100%" stopColor={color ?? COLORS.purple.DEFAULT} stopOpacity="0.4" />
            </LinearGradient>
          </Defs>
          <Circle
            cx={dimension / 2}
            cy={dimension / 2}
            r={radius}
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            fill="transparent"
            strokeDasharray={`${dashLength} ${gapLength}`}
          />
        </Svg>
      </Animated.View>
      {message ? (
        <Text
          style={{
            marginTop: SPACING.sm,
            fontFamily: FONTS.body,
            fontSize: 13,
            color: colors.textSecondary,
            textAlign: 'center',
          }}
          accessibilityLiveRegion="polite"
        >
          {message}
        </Text>
      ) : null}
    </View>
  )
}
